import React, { createContext, Dispatch, ReactNode, SetStateAction, useContext, useMemo, useState } from 'react';
import { ReactComponent as Deck } from "../../assets/icons/deck.svg";
import { ReactComponent as Location } from "../../assets/icons/location.svg";
import { ReactComponent as Chat } from "../../assets/icons/message.svg";
import { ReactComponent as Note } from "../../assets/icons/note.svg";
import { ReactComponent as Sectors } from "../../assets/icons/sector.svg";
import { ReactComponent as Sentence } from "../../assets/icons/sentence.svg";
import { ReactComponent as Stage } from "../../assets/icons/stage.svg";
import { ReactComponent as Status } from "../../assets/icons/status.svg";
import { ReactComponent as Thread } from "../../assets/icons/thread.svg";
import { ReactComponent as User } from "../../assets/icons/user.svg";
import { Dashboard, DealOwner, DealSource } from '../../types/files';
import storage from '../../utils/storage';
import { TableContext } from '../TableContext';

export type DashboardsTableFilters = {
  recent?: string[],
  statuses?: string[],
  stages?: string[],
  tagGroups?: ({title: string, tags: string[]})[],
  keyPeopleTags?: string[],
  locations?: string[],
  owners?: DealOwner[],
  sources?: DealSource[],
  added?: string[],
};

type DashboardsTableContextProps = {
  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  headers: string[] | undefined;
  setHeaders: Dispatch<SetStateAction<string[] | undefined>>;
  visibleHeaders: boolean[] | undefined;
  setVisibleHeaders: Dispatch<SetStateAction<boolean[] | undefined>>;
  columns: string[] | undefined;
  setColumns: Dispatch<SetStateAction<string[] | undefined>>;
  rows: Dashboard[] | undefined;
  setRows: Dispatch<SetStateAction<Dashboard[] | undefined>>;
  filters: DashboardsTableFilters;
  setFilters: Dispatch<SetStateAction<DashboardsTableFilters>>;
  preFiltered: boolean;
  setPreFiltered: Dispatch<SetStateAction<boolean>>;
  sortBy: string;
  setSortBy: Dispatch<SetStateAction<string>>;
  modalContext: any;
  setModalContext: Dispatch<SetStateAction<any>>;
};

export const InitDashboardsTable: DashboardsTableContextProps = {
  loading: false,
  setLoading: () => {},
  columns: [],
  setColumns: () => {},
  headers: [],
  setHeaders: () => {},
  visibleHeaders: [],
  setVisibleHeaders: () => {},
  rows: undefined,
  setRows: () => {},
  filters: {},
  setFilters: () => {},
  preFiltered: false,
  setPreFiltered: () => {},
  sortBy: 'Added',
  setSortBy: () => {},
  modalContext: {},
  setModalContext: () => {},
};

export const DashboardsTableContext = createContext<DashboardsTableContextProps>(InitDashboardsTable);

export const TableHeaders = ['Name', 'Recent Activity', 'Status', 'Stage', 'Sectors', 'Key People', 'Deck/Teaser', 'Location', 'Owner', 'Source', 'Connected Apps', 'LinkedIn Posts', 'Communications', 'Commentary', 'Description', 'Sync Status', 'Updated', 'Added'];
export const RowActions = ['Move to', 'Copy deals snippet', 'Update deal owner', 'Generate one-pager (PDF)', 'Export to CSV', 'Delete'];

export const HeaderIcons: {[header: string]: JSX.Element} = {
  'Status': <Status style={{ width: 24, height: 24, stroke: 'rgb(163, 167, 172)' }} />,
  'Stage': <Stage style={{ width: 24, height: 24, stroke: 'rgb(163, 167, 172)' }} />,
  'Sectors': <Sectors style={{ width: 24, height: 24, stroke: 'rgb(163, 167, 172)' }} />,
  'Key People': <User style={{ width: 24, height: 24, stroke: 'rgb(163, 167, 172)' }} />,
  'Deck/Teaser': <Deck style={{ width: 24, height: 24, stroke: 'rgb(163, 167, 172)' }} />,
  'Owner': <User style={{ width: 24, height: 24, stroke: 'rgb(163, 167, 172)' }} />,
  'Source': <User style={{ width: 24, height: 24, stroke: 'rgb(163, 167, 172)' }} />,
  'LinkedIn Posts': <Thread style={{ width: 24, height: 24, stroke: 'rgb(163, 167, 172)' }} />,
  'Communications': <Chat style={{ width: 24, height: 24, stroke: 'rgb(163, 167, 172)' }} />,
  'Commentary': <Note style={{ width: 24, height: 24, stroke: 'rgb(163, 167, 172)', strokeWidth: '0.25' }} />,
  'Description': <Sentence style={{ width: 24, height: 24, stroke: 'rgb(163, 167, 172)' }} />,
  'Location': <Location style={{ width: 24, height: 24, stroke: 'rgb(163, 167, 172)' }} />,
  'Geography': <Location style={{ width: 24, height: 24, stroke: 'rgb(163, 167, 172)' }} />,
};

const DashboardsTableProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { headers, columns, visibleHeaders, setColumns, setHeaders, setVisibleHeaders } = useContext(TableContext);
  const [loading, setLoading] = useState<boolean>(true);
  const [rows, setRows] = useState<Dashboard[] | undefined>(undefined);
  const [filters, setFilters] = useState<DashboardsTableFilters>({
    statuses: [], stages: [], tagGroups: [], locations: [], owners: [], sources: [], added: []
  });
  const [preFiltered, setPreFiltered] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<string>('Recent Activity');
  const [modalContext, setModalContext] = useState<any>({});

  const contextValue = useMemo(() => ({
    loading,
    setLoading,
    headers,
    setHeaders,
    columns,
    setColumns,
    visibleHeaders,
    setVisibleHeaders,
    rows,
    setRows,
    filters,
    setFilters,
    preFiltered,
    setPreFiltered,
    sortBy,
    setSortBy,
    modalContext,
    setModalContext,
  }), [
    loading,
    setLoading,
    headers,
    setHeaders,
    columns,
    setColumns,
    visibleHeaders,
    setVisibleHeaders,
    rows,
    setRows,
    filters,
    setFilters,
    preFiltered,
    setPreFiltered,
    sortBy,
    setSortBy,
    modalContext,
    setModalContext,
  ]);

  if (storage.getItem('config.logger-context'))
    console.log("CTX", ["DashboardsTable", contextValue]);

  return (
    <DashboardsTableContext.Provider value={contextValue}>
      {children}
    </DashboardsTableContext.Provider>
  );
};

export default DashboardsTableProvider;
